import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { type ProcessingCutoffDto } from '@treasury/api/channel';
import { CheckException, ExceptionDecisionStatus } from '@treasury/domain/arp';
import { CheckExceptionService } from '@treasury/domain/arp/check-exception.service';
import { InstitutionCheckExceptionReturnReason } from '@treasury/domain/arp/institution-check-exception-return-reason.entity';
import { Entitlements, EntitlementsService } from '@treasury/domain/entitlements';
import { TmContainer } from '@treasury/presentation';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-badge';
import '@treasury/presentation/components/tm-button';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
import '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-tabs';
import { MediaService } from '@treasury/utils/services/media-service';
import '@vaadin/grid';
import '@vaadin/grid/vaadin-grid-column.js';
import { TextFieldValueChangedEvent } from '@vaadin/text-field';
import { format } from 'date-fns';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../../utilities/table-result-filter';
import { HeaderBarService } from '../../../services/jhd-header-bar.service';
import '../../components/decision-toggle';
import { CheckExceptionFilterTabId } from '../../types';
import { exceptionNeedsDecision, exceptionsDecidedToday } from '../util/check-exception-filters';
import './check-exceptions-decision-workflow';

export const tagName = 'check-exceptions-container';
@customElement(tagName)
export class PositivePayExceptionsContainer extends TmContainer {
    @property()
    public checkExceptions: CheckException[] = [];

    @InjectProperty()
    declare private mediaService: MediaService;

    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private checkExceptionService: CheckExceptionService;

    @InjectProperty()
    declare private entitlementsService: EntitlementsService;

    @state()
    private showingExceptionDetail = false;

    @state()
    private selectedException?: CheckException;

    @state()
    private selectedFilterTab: CheckExceptionFilterTabId =
        CheckExceptionFilterTabId.ItemsToDecision;

    @state()
    private cutoffMessage?: string;

    @state()
    private cutoffTime?: ProcessingCutoffDto;

    @state()
    private returnReasons: InstitutionCheckExceptionReturnReason[] = [];

    @state()
    private showReviewWorkflow = false;

    @state()
    private showSelectAllOptions = false;

    @state()
    private hasReturnReason = false;

    @state()
    private searchTerm: string = '';

    @state()
    private tableHeaderExpanded = false;

    @state()
    private startTimeWarningRendered = false;

    @state()
    private modifiedExceptions: CheckException[] = [];

    public get exceptionType() {
        return window.location.pathname.split('/').pop();
    }

    public get returnReasonOptionDefault() {
        return this.returnReasons.find(reason => reason.isDefault);
    }

    public get headerTitle() {
        const returnReasonText =
            this.hasReturnReason &&
            this.selectedFilterTab !== CheckExceptionFilterTabId.ItemsDecisionedToday
                ? html`<p class="text-xs text-secondary break-words max-w-[85%]">
                      if no return reason is selected, the default return reason of
                      <strong>${this.returnReasonOptionDefault?.description}</strong> will be
                      applied at cutoff
                  </p>`
                : '';
        return this.checkExceptionService.isAfterCutoff && this.cutoffTime
            ? 'Decisioned Today'
            : html`Check Exceptions <br />${returnReasonText}`;
    }

    public get filteredExceptions() {
        let exceptions: CheckException[];

        switch (this.selectedFilterTab) {
            case CheckExceptionFilterTabId.AllItems:
                exceptions = this.checkExceptions;
                break;
            case CheckExceptionFilterTabId.ItemsDecisionedToday:
                exceptions = this.checkExceptions.filter(exceptionsDecidedToday);
                break;
            case CheckExceptionFilterTabId.ItemsToDecision:
                exceptions = this.checkExceptions.filter(exceptionNeedsDecision);
                break;
            default:
                exceptions = this.checkExceptions;
                break;
        }

        return filterTableResults(this.searchTerm, exceptions, [
            'accountNumber',
            'checkNumber',
            'paidAmount',
            'exceptionReason',
        ]);
    }

    /**
     * returns a list of items that are not modified
     */
    public get undecisionedItems() {
        return this.filteredExceptions.filter(
            exception => !exception.protected && !exception.decidedToday
        );
    }

    /**
     * returns items with decision currently in pay state
     */
    public get payItems() {
        return this.filteredExceptions.filter(
            exception => exception.decisionStatus === ExceptionDecisionStatus.Pay
        );
    }

    /**
     * returns items with decision currently in return state
     */
    public get returnItems() {
        return this.filteredExceptions.filter(
            exception => exception.decisionStatus === ExceptionDecisionStatus.Return
        );
    }

    public async firstUpdated() {
        await this.setup();
    }

    public render() {
        if (this.loading) {
            return html`${this.renderLoader()}`;
        }
        if (this.showReviewWorkflow) {
            return html`<check-exceptions-decision-workflow
                .checkExceptions=${this.modifiedExceptions}
                @endReviewWorkflow=${() => {
                    this.backToTableView();
                }}
                @refresh=${() => {
                    this.setup();
                }}
            >
            </check-exceptions-decision-workflow>`;
        }
        if (this.showingExceptionDetail) {
            return html`<check-exception-detail-container
                .checkException=${this.selectedException}
                .backToTableView=${() => this.backToTableView()}
                .returnReasons=${this.checkExceptionService.returnReasons}
                .isAfterCutoff=${this.checkExceptionService.isAfterCutoff}
            ></check-exception-detail-container>`;
        }
        return html`
            <tm-section
                class=${classMap({
                    'flex flex-col': true,
                    'overflow-hidden h-full': !this.isDesktop,
                    'mx-2 my-2 rounded-lg': this.isDesktop,
                })}
            >
                ${this.checkExceptionService.isAfterCutoff
                    ? nothing
                    : html`<tm-tabs
                          .tabs=${[
                              CheckExceptionFilterTabId.ItemsToDecision,
                              CheckExceptionFilterTabId.ItemsDecisionedToday,
                              CheckExceptionFilterTabId.AllItems,
                          ].map(val => ({ id: val, label: val }))}
                          .activeTab=${this.selectedFilterTab}
                          @switchTab=${(event: CustomEvent) => {
                              this.switchExceptionTab(event.detail.activeTab);
                              this.showSelectAllOptions = false;
                              this.clearAll();
                          }}
                          class="filter-tabs"
                      >
                      </tm-tabs>`}

                <tm-list-header
                    class=${classMap({
                        'h-[80px]': this.isDesktop,
                    })}
                    .headerTitle=${this.headerTitle}
                    wrapHeaderText
                    class="flex-1"
                    placeholder="Search"
                    @search-term-change=${(e: TextFieldValueChangedEvent) => {
                        this.searchTerm = e.detail.value;
                    }}
                >
                    ${this.renderHeaderSubtitle()}
                </tm-list-header>

                ${this.filteredExceptions.length === 0
                    ? html`<div class="w-full px-4 py-8 text-center text-sm text-secondary">
                          No check exceptions found
                      </div>`
                    : html`<check-exceptions-table
                          .readonly=${this.checkExceptionService.isAfterCutoff ||
                          this.checkExceptionService.isBeforeCutoff}
                          .checkExceptions=${this.filteredExceptions}
                          .setSelectedException=${(exception: CheckException) =>
                              this.setSelectedException(exception)}
                          @change=${() => {
                              this.setModifiedItems();
                          }}
                      ></check-exceptions-table>`}
                ${this.renderTotals()}${this.renderSelectAll()}
            </tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: this.modifiedExceptions.length
                            ? `Review (${this.modifiedExceptions.length})`
                            : 'Review',
                        onClick: () => {
                            if (this.checkExceptionService.isAfterCutoff) {
                                return;
                            }
                            this.checkExceptions.forEach(exception => (exception.readonly = true));
                            this.showReviewWorkflow = true;
                        },
                        disabled: !this.modifiedExceptions.length,
                    },
                ]}
                .visible=${!this.checkExceptionService.isAfterCutoff &&
                !this.checkExceptionService.isBeforeCutoff}
            ></tm-footer>
        `;
    }

    private async setup() {
        this.showSelectAllOptions = false;
        await this.checkExceptionService.ready();

        await this.tryFetch(
            async () =>
                Promise.all([
                    this.checkExceptionService.cutoffTime,
                    this.checkExceptionService.getCheckExceptions(),
                    this.checkExceptionService.returnReasons,
                    this.entitlementsService.isEnabled(Entitlements.HasReturnReason),
                ]),
            async ([cutoffTime, checkExceptions, returnReasons, allowReturnReason]) => {
                this.cutoffTime = cutoffTime;
                this.checkExceptions = checkExceptions;
                this.returnReasons = returnReasons;
                this.hasReturnReason = allowReturnReason;
            }
        );

        if (this.checkExceptionService.isAfterCutoff) {
            this.selectedFilterTab = CheckExceptionFilterTabId.AllItems;
        }
        this.initialHeaderConfig();
        this.switchExceptionTab(this.selectedFilterTab);
    }

    private toggleShowSelectAllOptions() {
        this.showSelectAllOptions = !this.showSelectAllOptions;
    }

    private payAll() {
        const exceptions = this.filteredExceptions;
        this.checkExceptions.forEach(exception => {
            if (exceptions.includes(exception)) {
                exception.pay();
                exception.modify();
            }
        });
        this.setModifiedItems();
    }

    private returnAll() {
        const exceptions = this.filteredExceptions;
        this.checkExceptions.forEach(exception => {
            if (exceptions.includes(exception)) {
                exception.return();
                exception.modify();
            }
        });
        this.setModifiedItems();
    }

    private clearAll() {
        const exceptions = this.filteredExceptions;
        this.checkExceptions.forEach(exception => {
            if (exceptions.includes(exception)) {
                exception.clearDecision();
            }
        });
        this.setModifiedItems();
    }

    private initialHeaderConfig() {
        this.headerService.configure({
            title: 'Positive Pay - Checks',
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.CheckExceptions}
                        .disabled=${!this.filteredExceptions?.length}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
        });
    }

    private switchExceptionTab(tabId: CheckExceptionFilterTabId) {
        if (tabId === this.selectedFilterTab) {
            return;
        }
        this.selectedFilterTab = tabId;
    }

    private setSelectedException(exception: CheckException) {
        this.selectedException = exception;
        this.showingExceptionDetail = true;
    }

    private setModifiedItems() {
        this.modifiedExceptions = this.filteredExceptions.filter(
            exception => !exception.protected && exception.decisionModified
        );
    }

    private backToTableView() {
        this.initialHeaderConfig();
        this.showingExceptionDetail = false;
        this.showReviewWorkflow = false;
        this.showSelectAllOptions = false;
        this.setModifiedItems();
    }

    private patchClonedExceptions(clonedExceptions: CheckException[]) {
        this.checkExceptions = this.checkExceptions.map(
            exception => clonedExceptions.find(clone => clone.guid === exception.guid) ?? exception
        );
    }

    private renderSelectAll() {
        if (this.checkExceptionService.isAfterCutoff) {
            return nothing;
        }

        return this.showSelectAllOptions
            ? html`<div class="flex mx-4 my-2 select-all-options">
                  <tm-button
                      class="mr-2"
                      @click=${() => {
                          this.toggleShowSelectAllOptions();
                          this.clearAll();
                      }}
                  >
                      Cancel
                  </tm-button>
                  <tm-button
                      class="mr-2"
                      importance="primary"
                      success
                      @click=${() => {
                          this.toggleShowSelectAllOptions();
                          this.payAll();
                      }}
                  >
                      Pay All
                  </tm-button>
                  <tm-button
                      class="mr-2"
                      importance="primary"
                      error
                      @click=${() => {
                          this.toggleShowSelectAllOptions();
                          this.returnAll();
                      }}
                  >
                      Return All
                  </tm-button>
              </div>`
            : html`<div class="flex mx-4 my-2 select-all-options">
                  <tm-button importance="secondary" @click=${this.toggleShowSelectAllOptions}>
                      Select All
                  </tm-button>
              </div>`;
    }

    private renderHeaderSubtitle() {
        if (this.checkExceptionService.isAfterCutoff && this.cutoffTime) {
            const timeArray = this.cutoffTime.cutoffTime.split(':');
            const [hours, minutes, seconds] = timeArray;
            const date = new Date();
            date.setHours(Number(hours));
            date.setMinutes(Number(minutes));
            date.setSeconds(Number(seconds));
            const formattedTime = format(date, 'h:mm a');
            return html`<div class="mr-1">
                <h3 class="text-xs text-secondary">Past Cut-off ${formattedTime}</h3>
            </div>`;
        }
        if (
            this.checkExceptionService.isBeforeCutoff &&
            this.cutoffTime?.startTime &&
            !this.startTimeWarningRendered
        ) {
            const timeArray = this.cutoffTime.startTime.split(':');
            const [hours, minutes, seconds] = timeArray;
            const date = new Date();
            date.setHours(Number(hours));
            date.setMinutes(Number(minutes));
            date.setSeconds(Number(seconds));
            const formattedTime = format(date, 'h:mm a');
            this.notificationService.renderWarning(`Decisions enabled at ${formattedTime}`);
            this.startTimeWarningRendered = true;
        }
        return nothing;
    }

    private renderTotals() {
        const undecisioned =
            this.selectedFilterTab === CheckExceptionFilterTabId.ItemsDecisionedToday
                ? nothing
                : html`<div class="p-1 flex items-center undecisioned-total text-sm">
                      <tm-badge>${this.undecisionedItems?.length ?? 0}</tm-badge>
                      <span class="table-footer-label p-1">Undecisioned</span>
                  </div>`;

        return html`<div class="w-full h-12 flex items-end justify-end border-b grid-footer">
            <div class="flex items-center border-t h-full">
                <div class="p-1 flex items-center total-count text-sm">
                    <tm-badge>${this.filteredExceptions?.length ?? 0}</tm-badge>
                    <span class="table-footer-label p-1">Total</span>
                </div>
                ${undecisioned}
                <div class="p-1 flex items-center pay-total text-sm">
                    <tm-badge>${this.payItems?.length ?? 0}</tm-badge>
                    <span class="table-footer-label p-1">Pay</span>
                </div>
                <div class="p-1 flex items-center return-total text-sm">
                    <tm-badge>${this.returnItems?.length ?? 0}</tm-badge>
                    <span class="table-footer-label p-1">Return</span>
                </div>
            </div>
        </div>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --lumo-space-xs: 0;
                    --lumo-space-m: 0;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: PositivePayExceptionsContainer;
    }
}
